import React, {
  createContext, useContext, useState, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import useUserContext from 'Context/UserContext';
import moment from 'moment';
import usePolling from 'Hooks/usePolling';
import { MESSAGES_STATS_SYNC_INTERVAL } from 'Consts/Messages';

const defaultValue = null;

export const MessagesStatsContext = createContext(defaultValue);

export function MessagesStatsContextProvider({ children }) {
  const { userResource } = useUserContext();
  const [lastRefresh, setLastRefresh] = useState();
  const [messagesStatsResource, setMessagesStatsResource] = useState(null);

  const lastRefreshRef = useRef();
  lastRefreshRef.current = lastRefresh;

  const handleMessagesStatsRefresh = async () => {
    if (!userResource?.relationship(fr.relationships.messagesStats.name)) {
      return;
    }
    try {
      let link = userResource.relationship(fr.relationships.messagesStats.name).link('related').copy();
      if (lastRefreshRef.current) {
        // Subtracting a few seconds to catch any that were posted during a refresh attempt
        // We handle duplicates when updating the thread list, and adding the last refresh reduces the
        // number of threads we may fetch in a single request
        link = link.equal('lastRefresh', lastRefreshRef.current.add(-1 * 3, 'second').toISOString());
      }
      const [messagesStatsRes] = await link.get();
      setMessagesStatsResource(messagesStatsRes);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setLastRefresh(moment());
    }
  };

  usePolling(() => handleMessagesStatsRefresh(), MESSAGES_STATS_SYNC_INTERVAL);

  useEffect(() => {
    if (userResource) {
      handleMessagesStatsRefresh();
    }
  }, [userResource]);

  return (
    <MessagesStatsContext.Provider
      value={{
        messagesStatsResource,
        lastRefresh,
        handleMessagesStatsRefresh,
        setLastRefresh,
        unreadThreadCount: parseInt(messagesStatsResource?.attr('unreadThreadCount') ?? '0', 10),
      }}
    >
      {children}
    </MessagesStatsContext.Provider>
  );
}

MessagesStatsContextProvider.propTypes = {
  children: PropTypes.any.isRequired,
};

function useMessagesStatsContext() {
  const context = useContext(MessagesStatsContext);

  if (!context) throw new Error('useMessagesStatsContext must be called from a MessagesStatsContext.Provider');

  return context;
}

export default useMessagesStatsContext;
